import { useNavigate } from "react-router-dom";
import ButtonComponent from "../../../../components/button/button";
import { useState, useEffect } from "react";
import InputComponent from "../../../../components/input/input";
import { getMe, postUser } from "../../../../features/search/service/user-service";
import { getNonSuperAdminRoles, getRoles, getRoleById } from "../../../../features/search/service/role-service";
import { getCompaniesListSelect } from "../../../../features/search/service/company-service";
import { SelectOption } from "../../../../shared/types/select-options";
import SelectComponent from "../../../../components/input/select";
import { MenuTop } from '../../../../components/menu/top/menu-top';
import './styles/add.css';
import { ROLES } from "../../../../shared/consts/roles";
import { useTranslation } from "../../../../shared/i18n/i18n-context";
import { PageBreadcrumb } from "../../../../components/page-breadcrumb/page-breadcrumb";
import { ProjectLoader } from "../../../../components/project-loader/project-loader";

export const AdminUsersAdd = () => {
    const navigate = useNavigate();
    const { t } = useTranslation('admin');
    const { t: menuT } = useTranslation('menu');
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [roleId, setRoleId] = useState<string>('');
    const [companyId, setCompanyId] = useState<string>('');
    const [roles, setRoles] = useState<SelectOption[]>([]);
    const [companies, setCompanies] = useState<SelectOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingData, setLoadingData] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [isSuperAdminUser, setIsSuperAdminUser] = useState<boolean>(false);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setLoadingData(true);
                setError('');
                
                const userData = await getMe();

                if (!userData) {
                    throw new Error(t('loadUserDataError'));
                }

                let rolesData: SelectOption[] = [];
                let companiesData: SelectOption[] = [];

                // Buscar a role do usuário pelo role_id
                const userRole = userData.role || (userData.role_id ? await getRoleById(userData.role_id) : null);

                const userRoleDescription = userRole?.description?.toLowerCase().trim();
                const userRoleName = userRole?.name?.toLowerCase().trim();
                const isSuperAdmin = userRoleDescription === ROLES.SUPER_ADMIN.toLowerCase() || userRoleName === ROLES.SUPER_ADMIN;
                const isAdmin = userRoleDescription === ROLES.ADMIN.toLowerCase() || userRoleName === ROLES.ADMIN;
                const isCompany = userRoleDescription === ROLES.EMPRESA || userRoleName === ROLES.EMPRESA;
                setIsSuperAdminUser(isSuperAdmin);

                if (isSuperAdmin) {
                    const [rolesResponse, companiesResponse] = await Promise.all([
                        getRoles(),
                        getCompaniesListSelect()
                    ]);
                    
                    rolesData = rolesResponse;
                    companiesData = companiesResponse;
                    
                } else if (isAdmin || isCompany) {
                    rolesData = await getNonSuperAdminRoles();
                    
                    if (userData?.companyId) {
                        companiesData = [{
                            id: userData.companyId,
                            label: userData.company?.name || t('myCompany')
                        }];
                    } else {
                        throw new Error(t('userWithoutCompany'));
                    }
                } else {
                    throw new Error(t('insufficientPermission'));
                }
                
                if (rolesData && rolesData.length > 0) {
                    setRoles(rolesData);
                    setRoleId(String(rolesData[0].id));
                } else {
                    setError(t('noAccessLevelAvailable'));
                }

                if (companiesData && companiesData.length > 0) {
                    setCompanies(companiesData);
                    setCompanyId(String(companiesData[0].id));
                } else {
                    setError(t('noCompanyAvailable'));
                }
                
            } catch (err: any) {
                console.error('Erro ao buscar dados iniciais:', err);
                setError(err.message || t('loadDataPermissionError'));
            } finally {
                setLoadingData(false);
            }
        };

        fetchInitialData();
    }, [t]);

    const handleAddUser = async () => {
        if (!name || name.trim() === '') {
            alert(t('fillName'));
            return;
        }

        if (!email || email.trim() === '') {
            alert(t('fillEmail'));
            return;
        }

        if (!password || password.trim() === '') {
            alert(t('fillPassword'));
            return;
        }

        if (!roleId || roleId === '' || roleId === 'undefined') {
            alert(t('selectAccessLevel'));
            return;
        }

        if (!companyId || companyId === '' || companyId === 'undefined') {
            alert(t('selectCompany'));
            return;
        }

        const payload = {
            name: name.trim(),
            email: email.trim(),
            password,
            roleId: roleId,
            companyId: companyId,
        };

        setLoading(true);

        try {
            const user = await postUser(payload);
            
            setName('');
            setEmail('');
            setPassword('');

            if (user) {
                navigate('/admin-painel/users');
            }
            
        } catch (error) {
            console.error('Erro ao adicionar usuário:', error);
            alert(t('createUserError'));
        } finally {
            setLoading(false);
        }
    };

    const isFormValid = () => {
        return (
            name.trim() !== '' &&
            email.trim() !== '' &&
            password.trim() !== '' &&
            roleId && roleId !== '' && roleId !== 'undefined' &&
            companyId && companyId !== '' && companyId !== 'undefined'
        );
    };
    const adminRootPath = isSuperAdminUser ? '/admin-painel-causale' : '/admin-painel';
    const adminRootLabel = isSuperAdminUser ? t('causalePanel') : t('adminPanel');

    if (loadingData) {
        return (
            <div>
                <MenuTop />
                <div className="add-user-container">
                    <ProjectLoader label={t('loadingData')} />
                </div>
            </div>
        );
    }

    if (error && roles.length === 0 && companies.length === 0) {
        return (
            <div>
                <MenuTop />
                <div className="add-user-container">
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center',
                        minHeight: '300px',
                        color: '#ff6b6b',
                        gap: '20px'
                    }}>
                        <h2>⚠️ {t('loadDataErrorTitle')}</h2>
                        <p>{error}</p>
                        <ButtonComponent 
                            label={t('back')}
                            onClick={() => navigate('/admin-painel/users')}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <MenuTop />
            <div className="add-user-container">
                <PageBreadcrumb
                    items={[
                        { label: menuT('home'), to: '/home' },
                        { label: adminRootLabel, to: adminRootPath },
                        { label: t('users'), to: '/admin-painel/users' },
                        { label: t('addUser') }
                    ]}
                />
                <h1>{t('addUser')}</h1>

                <InputComponent
                    id="id-input-name"
                    label={`${t('name')} *`}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                />
                
                <InputComponent
                    id="id-input-email"
                    label={`${t('email')} *`}
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                />
                
                <InputComponent
                    id="id-input-password"
                    label={`${t('password')} *`}
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                />

                {companies.length > 0 ? (
                    <SelectComponent
                        label={`${t('company')} *`}
                        value={companyId || ''}
                        onChange={(value) => {
                            setCompanyId(String(value));
                        }}
                        options={companies}
                        styles={{ marginTop: '10px' }}
                    />
                ) : (
                    <p style={{ color: '#ff6b6b', fontSize: '14px', marginTop: '10px' }}>
                        ⚠️ {t('noCompaniesPermission')}
                    </p>
                )}

                {roles.length > 0 ? (
                    <SelectComponent
                        label={`${t('accessLevel')} *`}
                        value={roleId || ''}
                        onChange={(value) => {
                            setRoleId(String(value));
                        }}
                        options={roles}
                        styles={{ marginTop: '10px' }}
                    />
                ) : (
                    <p style={{ color: '#ff6b6b', fontSize: '14px', marginTop: '10px' }}>
                        ⚠️ {t('noAccessLevelPermission')}
                    </p>
                )}

                <div className="button-container">
                    <ButtonComponent
                        label={loading ? t('creating') : t('createUser')}
                        onClick={handleAddUser}
                        disabled={loading || !isFormValid()}
                    />
                </div>

                <p style={{ color: '#999', fontSize: '12px', marginTop: '10px' }}>
                    {t('requiredFieldsHint')}
                </p>
            </div>
        </div>
    );
}
